import React from 'react'
import { useNavigate } from 'react-router-dom'
import Gap from './Gap'
import PlateCoverImage from './PlateCoverImage'
import { ReviewCategory } from '../api/user'
import HorizontalSlider from './HorizontalSlider'

export interface PlateListProps {
  categories: ReviewCategory[]
  onCategoryClick: (categoryId: string) => void
  isMine?: boolean
  /** true면 내부 "플레이트리스트" 헤더/추가버튼 숨김 (시트 등에서 재사용 시) */
  hideHeader?: boolean
  /** @deprecated 추가는 이제 풀페이지(/platelist/new)에서 처리, 복귀 시 부모 재마운트로 갱신됨 */
  onCategoryAdded?: () => void
}

const DESIGN_PLATE_LIST_ITEM_WIDTH = 170
const DESIGN_PLATE_LIST_ITEM_HEIGHT = 156
const DESIGN_PLATE_LIST_ITEM_PADDING_LEFT = 20
const DESIGN_PLATE_LIST_ITEM_PADDING_RIGHT = 16

const PlateList: React.FC<PlateListProps> = ({
  categories,
  onCategoryClick,
  isMine = false,
  hideHeader = false,
}) => {
  const navigate = useNavigate()

  const handleAddCategoryClick = () => {
    navigate('/platelist/new')
  }

  const renderPlateCoverImage = (imageUrl: string | null, alt: string) => {
    return (
      <div
        style={{
          position: 'absolute',
          top: `${(15.23 / DESIGN_PLATE_LIST_ITEM_HEIGHT) * 100}%`,
          // 접시를 카드 폭에 맞춘다.
          //
          // 예전 값: left/right -3.67px + width 169.34px.
          // left·right·width가 모두 지정되면 CSS는 right를 버린다. 원래 의도는 좌우로
          // 3.67px씩 삐져나오는 것(폭 177.34px)이었을 텐데 width가 같이 박혀 있어,
          // '왼쪽으로 3.67px 밀린' 결과만 남았다.
          //
          // 그 탓에 접시 중심(80.53px)과 이름 중심(카드 중심 84.50px)이 3.97px
          // 어긋나, 이름을 가운데 정렬해도 접시 기준으로는 오른쪽으로 치우쳐
          // 말줄임표가 접시 끝에 닿아 보였다. 이제 두 중심이 정확히 일치한다.
          left: 0,
          right: 0,
          width: '100%',
          height: `${(108.09 / DESIGN_PLATE_LIST_ITEM_HEIGHT) * 100}%`,
        }}
      >
        <PlateCoverImage imageUrl={imageUrl} isAddPlusImg={false} alt={alt} />
      </div>
    )
  }

  const renderPlateName = (title: string) => {
    return (
      // w-full이 없으면 말줄임이 걸리지 않는다.
      // 감싼 상자가 flex column + alignItems:center라 자식 폭이 '내용 크기'로 정해지는데,
      // 이름은 white-space:nowrap이라 글자만큼 넓어져 잘릴 여지가 사라진다.
      // (썸네일은 width:100%를 직접 줘서 이 문제를 피하고 있었다)
      //
      // 말줄임은 글자 수가 아니라 폭으로 정해진다. 여백이 없으면 이름이 카드 양 끝에
      // 닿을 때까지 늘어난 뒤에야 …이 붙어, 커버 이미지 폭(99.6%)보다 더 넓게 뻗기도 한다.
      // 여백만큼 상자를 좁혀 조금 일찍 줄인다.
      //
      // 좌우를 똑같이 두는 이유: 이름이 길든 짧든 가운데 정렬이어야 한다.
      // 한쪽만 넓히면 짧은 이름의 중심이 카드 중심에서 밀린다.
      // 말줄임을 더 일찍 걸고 싶으면 양쪽을 같이 키운다(상자가 좁아질수록 빨리 잘림).
      <div className="w-full px-20px text-center font-pretendard font-regular text-14px leading-125% text-brown-80%-main text-ellipsis-1">
        {title}
      </div>
    )
  }

  const renderPublicBadge = (isPublic?: boolean) => {
    if (isPublic) {
      return null
    }

    return (
      <>
        <Gap height={4} />
        <div className="font-pretendard font-regular text-12px leading-125% text-textColorText3">
          비공개
        </div>
      </>
    )
  }

  const renderGridItem = (
    title: string,
    icon: string | null,
    onClick: () => void,
    isPublic?: boolean
  ) => {
    return (
      <div
        className={`
          w-full
          flex
          flex-col
          items-center
          justify-center
          cursor-pointer
          select-none
          [-webkit-touch-callout:none]
        `}
        onClick={onClick}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            paddingBottom: `${(DESIGN_PLATE_LIST_ITEM_HEIGHT / DESIGN_PLATE_LIST_ITEM_WIDTH) * 100}%`,
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              height: '100%',
            }}
          >
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'end',
              }}
            >
              {renderPlateCoverImage(icon, title)}
              {renderPlateName(title)}
            </div>
          </div>
        </div>
        {renderPublicBadge(isPublic)}
      </div>
    )
  }

  if (categories.length === 0 && !isMine) {
    return null
  }

  return (
    <>
      {!hideHeader && (
        <>
          <div className="w-full px-20px flex items-center justify-center">
            <p className="flex-1 font-pretendard font-bold text-16px leading-125% text-brown-80%-main">
              플레이트리스트
            </p>

            <Gap width={8} />

            {/* 추가 버튼 - 내꺼인 경우에만 표시 */}
            {isMine && (
              <div
                data-tutorial="platelist-add"
                className="w-24px h-24px flex items-center justify-center cursor-pointer"
                onClick={handleAddCategoryClick}
              >
                <img
                  src="/images/bold-plus-icon.png"
                  alt="추가"
                  draggable={false}
                  className={`
                    w-full
                    h-full
                    object-contain
                    select-none
                    [-webkit-touch-callout:none]
                  `}
                />
              </div>
            )}
          </div>

          <Gap height={15.5} />
        </>
      )}

      {/* 카테고리 폴더 슬라이드 리스트 */}
      {categories.length > 0 && (
        <div
          style={{
            width: '100%',
            paddingLeft: `${DESIGN_PLATE_LIST_ITEM_PADDING_LEFT}px`,
            paddingRight: `${DESIGN_PLATE_LIST_ITEM_PADDING_RIGHT}px`,
          }}
        >
          <HorizontalSlider>
            {categories.map((category) => (
              <div key={category.id}>
                {renderGridItem(
                  category.name,
                  category.thumbnail_image || null,
                  () => {
                    onCategoryClick(category.id.toString())
                  },
                  category.is_public
                )}
              </div>
            ))}
          </HorizontalSlider>
        </div>
      )}
    </>
  )
}

export default PlateList
